
import React from "react";
import { Link } from "react-router-dom";
import { 
  Clock, Bookmark, ThumbsUp, ThumbsDown, 
  MessageCircle, Check, Terminal, Code, 
  Layers, ExternalLink, Copy, BookOpen,
  GitBranch, PackageOpen
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";

interface DevResourcesContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const DevResourcesContent: React.FC<DevResourcesContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  const renderDeveloperOverview = () => {
    const articleId = "developer-overview";
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Developer Overview</h1>
          <p className="text-gray-500 mt-2">
            Get started with developing on and integrating with the DGK platform.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 20, 2023</span>
            <Badge variant="outline" className="ml-2">Developer Resources</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="platform-architecture">Platform Architecture</h2>
          <p>
            The DGK platform is built on a modern, scalable architecture designed to support extensibility 
            and integration. Understanding the architecture will help you effectively build on our platform.
          </p>

          <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-6 rounded-xl border border-slate-200 my-6">
            <div className="flex items-center mb-4">
              <div className="bg-slate-700 p-2 rounded-full mr-3">
                <Layers className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800">Platform Layers</h3>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-medium text-lg mb-2">API Layer</h4>
                <p className="text-slate-600 text-sm">
                  RESTful and GraphQL APIs that allow you to interact with platform data and services.
                  All external communication with the platform happens through this layer.
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-medium text-lg mb-2">Service Layer</h4>
                <p className="text-slate-600 text-sm">
                  Core business logic and services that power the platform. This includes user management,
                  project handling, communications, payments, and other essential functionalities.
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-medium text-lg mb-2">Data Layer</h4>
                <p className="text-slate-600 text-sm">
                  Persistent storage for all platform data. We use a combination of relational databases,
                  document stores, and specialized data solutions depending on the use case.
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-medium text-lg mb-2">Integration Layer</h4>
                <p className="text-slate-600 text-sm">
                  Webhooks, event streams, and integration points that allow the platform to communicate
                  with external systems and services.
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-medium text-lg mb-2">Extension Framework</h4>
                <p className="text-slate-600 text-sm">
                  Tools and interfaces for building custom extensions and applications that integrate
                  with the platform's core functionality.
                </p>
              </div>
            </div>
          </div>

          <h2 id="developer-tools">Developer Tools</h2>
          <p>
            We provide a suite of tools to help you develop, test, and deploy your integrations with the platform.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
              <div className="flex items-center mb-3">
                <Terminal className="h-5 w-5 text-slate-700 mr-2" />
                <h3 className="font-medium">Developer Console</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                A web-based interface for managing your API keys, applications, and testing API endpoints.
                Access it at <code>developer.dgk-platform.com</code>.
              </p>
              <Button variant="outline" size="sm" className="mt-2">
                Access Developer Console
              </Button>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
              <div className="flex items-center mb-3">
                <Code className="h-5 w-5 text-slate-700 mr-2" />
                <h3 className="font-medium">SDKs & Libraries</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Official client libraries for various programming languages to simplify integration with our APIs.
                Currently supported: JavaScript, Python, Ruby, Java, and PHP.
              </p>
              <Button variant="outline" size="sm" className="mt-2">
                Browse SDKs
              </Button>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
              <div className="flex items-center mb-3">
                <BookOpen className="h-5 w-5 text-slate-700 mr-2" />
                <h3 className="font-medium">API Playground</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Interactive API documentation that allows you to test API calls directly in your browser.
                Experiment with endpoints before writing any code.
              </p>
              <Button variant="outline" size="sm" className="mt-2">
                Open API Playground
              </Button>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
              <div className="flex items-center mb-3">
                <GitBranch className="h-5 w-5 text-slate-700 mr-2" />
                <h3 className="font-medium">Sandbox Environment</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                A fully functional testing environment that mimics production but uses test data.
                Perfect for developing and testing your integrations safely.
              </p>
              <Button variant="outline" size="sm" className="mt-2">
                Set Up Sandbox
              </Button>
            </div>
          </div>

          <h2 id="getting-started-for-developers">Getting Started</h2>
          <p>
            Follow these steps to begin developing with the DGK platform:
          </p>

          <div className="my-6 space-y-6">
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-700 font-medium">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Create a Developer Account</h3>
                <p className="text-gray-600 mb-3">
                  Register for a developer account to access developer tools and resources.
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <ul className="text-sm space-y-1 list-disc pl-5">
                    <li>Go to <code>developer.dgk-platform.com</code></li>
                    <li>Click "Sign Up" and follow the registration process</li>
                    <li>Verify your email address</li>
                    <li>Set up 2FA for enhanced security (recommended)</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-700 font-medium">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Create Your First Application</h3>
                <p className="text-gray-600 mb-3">
                  Set up an application to get API credentials and configure OAuth settings.
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <ul className="text-sm space-y-1 list-disc pl-5">
                    <li>Go to the Developer Console</li>
                    <li>Select "Applications" from the menu</li>
                    <li>Click "Create New Application"</li>
                    <li>Fill in the required information (name, description, etc.)</li>
                    <li>Configure redirect URIs for OAuth authentication</li>
                    <li>Set permissions your application will need</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-700 font-medium">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Explore APIs and Documentation</h3>
                <p className="text-gray-600 mb-3">
                  Familiarize yourself with available APIs and their capabilities.
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <ul className="text-sm space-y-1 list-disc pl-5">
                    <li>Browse the API reference documentation</li>
                    <li>Use the API Playground to experiment with endpoints</li>
                    <li>Review example applications and code samples</li>
                    <li>Join the developer forum to connect with other developers</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-700 font-medium">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Develop and Test Your Integration</h3>
                <p className="text-gray-600 mb-3">
                  Build your integration using our SDKs and test environment.
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <ul className="text-sm space-y-1 list-disc pl-5">
                    <li>Install the appropriate SDK for your programming language</li>
                    <li>Configure your application with test API keys</li>
                    <li>Use the sandbox environment for testing</li>
                    <li>Implement authentication using OAuth 2.0</li>
                    <li>Test your integration thoroughly</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-700 font-medium">
                  5
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Deploy to Production</h3>
                <p className="text-gray-600 mb-3">
                  Move your integration from testing to production.
                </p>
                <div className="bg-gray-50 p-4 rounded-md">
                  <ul className="text-sm space-y-1 list-disc pl-5">
                    <li>Request production API credentials through the Developer Console</li>
                    <li>Update your application with production API keys</li>
                    <li>Implement appropriate monitoring and error handling</li>
                    <li>Follow security best practices</li>
                    <li>Launch your integration!</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-8">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Terminal className="h-5 w-5 mr-2" />
              Sample Applications
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              Check out our sample applications to see real-world examples of platform integrations.
            </p>
            <Button size="sm">
              View Sample Applications
            </Button>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted[articleId] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes(articleId) ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark(articleId)}
            >
              {bookmarkedArticles.includes(articleId) ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderSDKLibraries = () => {
    const articleId = "sdk-libraries";
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">SDK & Libraries</h1>
          <p className="text-gray-500 mt-2">
            Official libraries to help you integrate with the DGK platform in your preferred programming language.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 15, 2023</span>
            <Badge variant="outline" className="ml-2">Developer Resources</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="available-sdks">Available SDKs</h2>
          <p>
            We provide official SDKs for popular programming languages to simplify integration with the DGK Platform API.
            Each SDK offers a consistent interface to our services, handling authentication, request formatting, and error handling for you.
          </p>

          <Tabs defaultValue="javascript" className="mt-6">
            <TabsList className="grid grid-cols-5 w-full max-w-3xl">
              <TabsTrigger value="javascript">JavaScript</TabsTrigger>
              <TabsTrigger value="python">Python</TabsTrigger>
              <TabsTrigger value="java">Java</TabsTrigger>
              <TabsTrigger value="php">PHP</TabsTrigger>
              <TabsTrigger value="ruby">Ruby</TabsTrigger>
            </TabsList>
            
            <TabsContent value="javascript" className="pt-4">
              <div className="bg-slate-50 p-5 rounded-md border border-slate-200">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <svg viewBox="0 0 24 24" width="24" height="24" className="mr-2">
                      <path fill="#F7DF1E" d="M0 0h24v24H0z"/>
                      <path d="M19.6 19.5c-.4.8-1.2 1.4-2.1 1.8-1.2.5-2.4.5-3.6 0-.9-.4-1.7-1-2.2-1.9l2.2-1.3c.2.4.5.7.9.9.4.2.8.1 1.2 0 .3-.1.5-.4.5-.7v-8.7h3v9.9zm-7.9.5c-.7.7-1.6 1.1-2.6 1.3-1.2.2-2.4 0-3.4-.6-.8-.5-1.4-1.3-1.7-2.2l2.3-1.3c.1.4.3.8.6 1.1.3.2.6.3 1 .3.3 0 .7-.1.9-.3.2-.2.3-.5.3-.8 0-.4-.3-.7-.6-.9-.3-.1-2.2-.6-2.6-.8-1.1-.4-1.9-1.3-2.1-2.4-.1-.8 0-1.6.4-2.3.4-.7 1-1.2 1.7-1.4 1.6-.5 3.3-.1 4.5 1.1L8.4 12c-.2-.3-.4-.5-.7-.7-.3-.1-.6-.1-1 0-.2.1-.4.3-.4.5 0 .4.5.6.8.7.3.1 2.2.6 2.6.7 1.3.4 2.2 1.4 2.3 2.8.1.8-.1 1.7-.6 2.4l-.7.6z" fill="#000"/>
                    </svg>
                    <h3 className="font-medium">JavaScript/TypeScript SDK</h3>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    v3.2.1
                  </Badge>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm">Installation</h4>
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
                    npm install @dgk-platform/sdk
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm">Quick Start Example</h4>
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
{`import { DGKClient } from '@dgk-platform/sdk';

// Initialize the client
const client = new DGKClient({
  apiKey: 'YOUR_API_KEY',
  environment: 'sandbox' // or 'production'
});

// Example: Fetch projects
async function getProjects() {
  try {
    const projects = await client.projects.list({
      status: 'active',
      limit: 10
    });
    console.log(projects);
  } catch (error) {
    console.error('Error fetching projects:', error);
  }
}`}
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <Button size="sm" variant="outline" className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Full Documentation
                  </Button>
                  <Button size="sm" variant="outline" className="flex items-center">
                    <GitBranch className="h-4 w-4 mr-2" />
                    GitHub Repository
                  </Button>
                  <Button size="sm" variant="outline" className="flex items-center">
                    <PackageOpen className="h-4 w-4 mr-2" />
                    NPM Package
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="python" className="pt-4">
              <div className="bg-slate-50 p-5 rounded-md border border-slate-200">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <svg viewBox="0 0 24 24" width="24" height="24" className="mr-2">
                      <path fill="#3776AB" d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 2c4.972 0 9 4.028 9 9a8.96 8.96 0 0 1-2.051 5.731c-1.223-2.345-3.291-3.973-5.68-4.668.566-.655.939-1.428.939-2.235 0-1.657-1.343-3-3-3s-3 1.343-3 3c0 .807.373 1.58.939 2.235-2.389.695-4.457 2.323-5.68 4.668A8.96 8.96 0 0 1 3 11c0-4.972 4.028-9 9-9z"/>
                    </svg>
                    <h3 className="font-medium">Python SDK</h3>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    v2.4.0
                  </Badge>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm">Installation</h4>
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
                    pip install dgk-platform-sdk
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm">Quick Start Example</h4>
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
{`from dgk_platform import Client

# Initialize the client
client = Client(
    api_key='YOUR_API_KEY',
    environment='sandbox'  # or 'production'
)

# Example: Fetch projects
def get_projects():
    try:
        projects = client.projects.list(
            status='active',
            limit=10
        )
        print(projects)
    except Exception as e:
        print(f"Error fetching projects: {e}")

get_projects()`}
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <Button size="sm" variant="outline" className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Full Documentation
                  </Button>
                  <Button size="sm" variant="outline" className="flex items-center">
                    <GitBranch className="h-4 w-4 mr-2" />
                    GitHub Repository
                  </Button>
                  <Button size="sm" variant="outline" className="flex items-center">
                    <PackageOpen className="h-4 w-4 mr-2" />
                    PyPI Package
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="java" className="pt-4">
              <div className="bg-slate-50 p-5 rounded-md border border-slate-200">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <svg viewBox="0 0 24 24" width="24" height="24" className="mr-2">
                      <path fill="#5382A1" d="M8.851 18.56c0 0-0.92 0.534 0.657 0.714 1.902 0.218 2.874 0.187 4.969-0.211 0 0 0.552 0.346 1.321 0.646-4.699 2.013-10.633-0.118-6.947-1.149M8.276 15.933c0 0-1.030 0.764 0.545 0.927 2.042 0.21 3.653 0.228 6.444-0.308 0 0 0.384 0.389 0.987 0.602-5.679 1.661-12.007 0.13-7.976-1.221M13.116 11.475c1.158 1.333-0.304 2.533-0.304 2.533s2.939-1.518 1.589-3.418c-1.261-1.772-2.228-2.652 3.007-5.688 0-0.001-8.216 2.051-4.292 6.573M19.33 20.504c0 0 0.679 0.559-0.747 0.991-2.712 0.822-11.288 1.069-13.669 0.033-0.856-0.373 0.75-0.89 1.254-0.998 0.527-0.114 0.828-0.093 0.828-0.093-0.953-0.671-6.156 1.317-2.643 1.887 9.58 1.553 17.462-0.717 14.977-1.82M9.292 13.21c0 0-4.362 1.036-1.544 1.412 1.189 0.159 3.561 0.123 5.77-0.062 1.806-0.152 3.618-0.477 3.618-0.477s-0.637 0.272-1.098 0.587c-4.429 1.165-12.986 0.623-10.522-0.569 2.082-1.006 3.776-0.891 3.776-0.891M17.116 17.584c4.503-2.34 2.421-4.589 0.967-4.286-0.355 0.074-0.515 0.138-0.515 0.138s0.132-0.207 0.385-0.297c2.875-1.011 5.086 2.981-0.928 4.562 0-0.001 0.07-0.062 0.091-0.117M14.401 0c0 0 2.494 2.494-2.365 6.33-3.896 3.077-0.888 4.832-0.001 6.836-2.274-2.053-3.943-3.858-2.824-5.539 1.644-2.469 6.197-3.665 5.19-7.627M9.734 23.924c4.322 0.277 10.959-0.153 11.116-2.198 0 0-0.302 0.775-3.572 1.391-3.688 0.694-8.239 0.613-10.937 0.168 0-0.001 0.553 0.457 3.393 0.639"/>
                    </svg>
                    <h3 className="font-medium">Java SDK</h3>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    v2.1.3
                  </Badge>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm">Maven Installation</h4>
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
{`<dependency>
  <groupId>com.dgk-platform</groupId>
  <artifactId>dgk-platform-sdk</artifactId>
  <version>2.1.3</version>
</dependency>`}
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm">Quick Start Example</h4>
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="bg-gray-900 text-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
{`import com.dgk.platform.Client;
import com.dgk.platform.models.Project;
import com.dgk.platform.exceptions.DGKException;

public class DGKExample {
    public static void main(String[] args) {
        // Initialize the client
        Client client = new Client.Builder()
            .setApiKey("YOUR_API_KEY")
            .setEnvironment(Client.Environment.SANDBOX)
            .build();
        
        // Example: Fetch projects
        try {
            List<Project> projects = client.projects()
                .list()
                .withStatus("active")
                .withLimit(10)
                .execute();
                
            for (Project project : projects) {
                System.out.println(project.getTitle());
            }
        } catch (DGKException e) {
            System.err.println("Error fetching projects: " + e.getMessage());
        }
    }
}`}
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <Button size="sm" variant="outline" className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Full Documentation
                  </Button>
                  <Button size="sm" variant="outline" className="flex items-center">
                    <GitBranch className="h-4 w-4 mr-2" />
                    GitHub Repository
                  </Button>
                  <Button size="sm" variant="outline" className="flex items-center">
                    <PackageOpen className="h-4 w-4 mr-2" />
                    Maven Repository
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="php" className="pt-4">
              <div className="bg-slate-50 p-5 rounded-md border border-slate-200">
                <p className="text-center py-4">PHP SDK content here</p>
              </div>
            </TabsContent>
            
            <TabsContent value="ruby" className="pt-4">
              <div className="bg-slate-50 p-5 rounded-md border border-slate-200">
                <p className="text-center py-4">Ruby SDK content here</p>
              </div>
            </TabsContent>
          </Tabs>

          <h2 id="sdk-features" className="mt-8">Common SDK Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h3 className="font-medium text-lg mb-2">Authentication</h3>
              <ul className="text-sm space-y-1 list-disc pl-5">
                <li>API key authentication</li>
                <li>OAuth 2.0 support</li>
                <li>Session management</li>
                <li>Token refresh handling</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h3 className="font-medium text-lg mb-2">Error Handling</h3>
              <ul className="text-sm space-y-1 list-disc pl-5">
                <li>Standardized error responses</li>
                <li>Detailed error information</li>
                <li>Rate limit handling</li>
                <li>Automatic retries for transient failures</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h3 className="font-medium text-lg mb-2">Resource Management</h3>
              <ul className="text-sm space-y-1 list-disc pl-5">
                <li>Full CRUD operations for all resources</li>
                <li>Pagination handling</li>
                <li>Filtering and sorting</li>
                <li>Resource relationships</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h3 className="font-medium text-lg mb-2">Advanced Features</h3>
              <ul className="text-sm space-y-1 list-disc pl-5">
                <li>Webhook signature verification</li>
                <li>Idempotency support</li>
                <li>Request/response logging</li>
                <li>Language-specific idioms</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-8">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <ExternalLink className="h-5 w-5 mr-2" />
              Community Libraries
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              In addition to our official SDKs, there are community-maintained libraries for other languages.
              While not officially supported, they can be useful for certain integrations.
            </p>
            <Button size="sm">
              Browse Community Libraries
            </Button>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted[articleId] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes(articleId) ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark(articleId)}
            >
              {bookmarkedArticles.includes(articleId) ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderIntegrations = () => {
    const articleId = "integrations";
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
          <p className="text-gray-500 mt-2">
            Connect your applications and services with the DGK platform using our integration options.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: September 5, 2023</span>
            <Badge variant="outline" className="ml-2">Developer Resources</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="integration-options">Integration Options</h2>
          <p>
            The DGK platform offers multiple integration methods to connect with your existing systems, 
            applications, and workflows. Choose the option that best fits your needs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-6">
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-lg font-medium mb-2">REST API</h3>
              <p className="text-sm text-gray-600 mb-4">
                Our comprehensive REST API gives you full access to platform resources and operations.
              </p>
              <ul className="text-sm space-y-2 list-disc pl-5 mb-4">
                <li>JSON-based request/response format</li>
                <li>OAuth 2.0 authentication</li>
                <li>Webhook support for event notifications</li>
                <li>Rate limiting controls</li>
              </ul>
              <Button size="sm" variant="outline" className="w-full">
                API Documentation
              </Button>
            </div>
            
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-lg font-medium mb-2">GraphQL API</h3>
              <p className="text-sm text-gray-600 mb-4">
                Query exactly the data you need with our flexible GraphQL API.
              </p>
              <ul className="text-sm space-y-2 list-disc pl-5 mb-4">
                <li>Custom queries to fetch only what you need</li>
                <li>Strongly typed schema</li>
                <li>Interactive GraphQL Playground</li>
                <li>Subscription support for real-time updates</li>
              </ul>
              <Button size="sm" variant="outline" className="w-full">
                GraphQL Documentation
              </Button>
            </div>
            
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-lg font-medium mb-2">Webhooks</h3>
              <p className="text-sm text-gray-600 mb-4">
                Receive real-time notifications when events occur on the platform.
              </p>
              <ul className="text-sm space-y-2 list-disc pl-5 mb-4">
                <li>Event-driven architecture</li>
                <li>Configurable event types</li>
                <li>Secure payload verification</li>
                <li>Retry mechanisms for failed deliveries</li>
              </ul>
              <Button size="sm" variant="outline" className="w-full">
                Webhook Documentation
              </Button>
            </div>
          </div>

          <h2 id="third-party-integrations">Third-Party Integrations</h2>
          <p>
            The DGK platform comes with pre-built integrations for popular third-party services.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="flex items-start p-4 bg-white rounded-lg border border-gray-200">
              <div className="bg-blue-100 p-2 rounded-md mr-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="4" fill="#2563EB" />
                  <path d="M7 12H17M17 12L12 7M17 12L12 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h3 className="font-medium mb-1">Slack Integration</h3>
                <p className="text-sm text-gray-600">
                  Post notifications, updates, and alerts to your Slack channels.
                </p>
                <Button variant="link" size="sm" className="px-0 py-1 h-auto">
                  Set up Slack →
                </Button>
              </div>
            </div>
            
            <div className="flex items-start p-4 bg-white rounded-lg border border-gray-200">
              <div className="bg-purple-100 p-2 rounded-md mr-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="4" fill="#8B5CF6" />
                  <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h3 className="font-medium mb-1">Microsoft Teams Integration</h3>
                <p className="text-sm text-gray-600">
                  Collaborate and receive notifications directly in Microsoft Teams.
                </p>
                <Button variant="link" size="sm" className="px-0 py-1 h-auto">
                  Set up Teams →
                </Button>
              </div>
            </div>
            
            <div className="flex items-start p-4 bg-white rounded-lg border border-gray-200">
              <div className="bg-green-100 p-2 rounded-md mr-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="4" fill="#10B981" />
                  <path d="M16 8L8 16M8 8L16 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h3 className="font-medium mb-1">Zapier Integration</h3>
                <p className="text-sm text-gray-600">
                  Connect DGK to 3,000+ apps with our Zapier integration.
                </p>
                <Button variant="link" size="sm" className="px-0 py-1 h-auto">
                  Set up Zapier →
                </Button>
              </div>
            </div>
            
            <div className="flex items-start p-4 bg-white rounded-lg border border-gray-200">
              <div className="bg-red-100 p-2 rounded-md mr-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="4" fill="#EF4444" />
                  <path d="M15 9L9 15M9 9L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h3 className="font-medium mb-1">Google Workspace Integration</h3>
                <p className="text-sm text-gray-600">
                  Connect with Google Calendar, Drive, and other Google services.
                </p>
                <Button variant="link" size="sm" className="px-0 py-1 h-auto">
                  Set up Google →
                </Button>
              </div>
            </div>
          </div>

          <h2 id="custom-integrations">Building Custom Integrations</h2>
          <p>
            For specialized integration needs, you can build custom solutions using our APIs and SDKs.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 my-6">
            <h3 className="font-medium mb-4">Integration Best Practices</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3">
                  1
                </div>
                <div>
                  <h4 className="font-medium mb-1">Use Webhooks for Real-time Events</h4>
                  <p className="text-sm text-gray-600">
                    Instead of polling our API for changes, use webhooks to be notified in real-time 
                    when relevant events occur. This is more efficient and gives you immediate updates.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3">
                  2
                </div>
                <div>
                  <h4 className="font-medium mb-1">Implement Proper Error Handling</h4>
                  <p className="text-sm text-gray-600">
                    Always handle API errors gracefully and implement appropriate retries with 
                    backoff strategies for transient failures.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3">
                  3
                </div>
                <div>
                  <h4 className="font-medium mb-1">Secure Your Integration</h4>
                  <p className="text-sm text-gray-600">
                    Protect API keys and secrets, validate webhook payloads, and follow security 
                    best practices to keep your integration secure.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3">
                  4
                </div>
                <div>
                  <h4 className="font-medium mb-1">Monitor Your Integration</h4>
                  <p className="text-sm text-gray-600">
                    Implement proper logging and monitoring to quickly identify and resolve 
                    any issues with your integration.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-5 rounded-lg border border-blue-200 mb-6">
            <h3 className="font-medium text-blue-800 mb-2">Need Help?</h3>
            <p className="text-sm text-blue-700 mb-4">
              Our team is here to help you build successful integrations with the DGK platform.
            </p>
            <div className="flex space-x-3">
              <Button size="sm" variant="outline" className="bg-white">
                Contact Support
              </Button>
              <Button size="sm" variant="outline" className="bg-white">
                Join Developer Forum
              </Button>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted[articleId] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes(articleId) ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark(articleId)}
            >
              {bookmarkedArticles.includes(articleId) ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (activePage === "developer-overview") {
    return renderDeveloperOverview();
  }

  if (activePage === "sdk-libraries") {
    return renderSDKLibraries();
  }
  
  if (activePage === "integrations") {
    return renderIntegrations();
  }

  return (
    <div className="flex items-center justify-center h-[400px] bg-gray-50 rounded-lg border border-dashed border-gray-300">
      <div className="text-center max-w-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Documentation Coming Soon</h3>
        <p className="text-gray-500">
          We're currently working on this section of the developer documentation. 
          Check back soon for updates or contact our developer support team for assistance.
        </p>
      </div>
    </div>
  );
};

export default DevResourcesContent;
